import {createItemPreparation} from '../journal/preparation.js';

export const RecipeItemScaleMode=Object.freeze({
  PROPORTIONAL:'proportional',
  FIXED:'fixed'
});

const SCALE_MODES=new Set(Object.values(RecipeItemScaleMode));
const CODE=/^[a-z][a-z0-9_]{1,63}$/;
const MASS_UNITS=new Set(['g','kg','oz','lb']);

function required(value,label,max=160){
  const text=String(value??'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function optionalText(value,label,max=4000){
  if(value==null||value==='')return null;
  const text=String(value).trim();
  if(!text)return null;
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function idOrGenerated(value){
  const id=String(value??'').trim();
  if(id)return id;
  const generated=globalThis.crypto?.randomUUID?.();
  if(generated)return generated;
  throw new TypeError('id is required when crypto.randomUUID is unavailable');
}

function stableCode(value,label){
  if(value==null||value==='')return null;
  const code=String(value).trim().toLowerCase();
  if(!CODE.test(code))throw new TypeError('Invalid '+label);
  return code;
}

function positive(value,label){
  if(value==null||value==='')return null;
  const number=Number(value);
  if(!Number.isFinite(number)||number<=0)throw new TypeError(label+' must be positive');
  return number;
}

function sortOrder(value){
  const number=Number(value??0);
  if(!Number.isInteger(number)||number<0||number>1000000)throw new TypeError('sortOrder is invalid');
  return number;
}

function scaleMode(value){
  const mode=String(value??RecipeItemScaleMode.PROPORTIONAL).trim().toLowerCase();
  if(!SCALE_MODES.has(mode))throw new TypeError('Unsupported recipe item scale mode');
  return mode;
}

export function createRecipeItem({
  id,
  recipeId,
  catalogueProductCode=null,
  customProductCode=null,
  name,
  referenceQuantity=null,
  referenceMass=null,
  massUnit=null,
  scaleMode:itemScaleMode=RecipeItemScaleMode.PROPORTIONAL,
  minScaleFactor=null,
  maxScaleFactor=null,
  scalingNotes=null,
  sortOrder:itemSortOrder=0
}={}){
  const catalogue=stableCode(catalogueProductCode,'catalogueProductCode');
  const custom=stableCode(customProductCode,'customProductCode');
  if(Boolean(catalogue)===Boolean(custom)){
    throw new TypeError('Exactly one catalogue or custom product code is required');
  }

  const quantity=positive(referenceQuantity,'referenceQuantity');
  const mass=positive(referenceMass,'referenceMass');
  if(quantity==null&&mass==null)throw new TypeError('Recipe item requires reference quantity or mass');

  const unit=massUnit==null||massUnit===''?null:String(massUnit).trim().toLowerCase();
  if((mass==null)!=(unit==null))throw new TypeError('referenceMass and massUnit must be provided together');
  if(unit&&!MASS_UNITS.has(unit))throw new TypeError('Invalid massUnit');

  const minimum=positive(minScaleFactor,'minScaleFactor');
  const maximum=positive(maxScaleFactor,'maxScaleFactor');
  if(minimum!=null&&maximum!=null&&maximum<minimum)throw new TypeError('maxScaleFactor cannot be below minScaleFactor');

  return Object.freeze({
    id:idOrGenerated(id),
    recipeId:required(recipeId,'recipeId',128),
    catalogueProductCode:catalogue,
    customProductCode:custom,
    name:required(name,'name',160),
    referenceQuantity:quantity,
    referenceMass:mass,
    massUnit:unit,
    scaleMode:scaleMode(itemScaleMode),
    minScaleFactor:minimum,
    maxScaleFactor:maximum,
    scalingNotes:optionalText(scalingNotes,'scalingNotes'),
    sortOrder:sortOrder(itemSortOrder)
  });
}

export function createRecipeItemPreparation({
  recipeItemId,
  curingMethodKey=null,
  curingMethodCustom=null,
  curingDurationMinutes=null,
  curingNotes=null,
  marinadeNotes=null,
  spicesNotes=null,
  dryingDurationMinutes=null,
  dryingNotes=null,
  preparationDurationMinutes=null,
  preparationNotes=null
}={}){
  const itemId=required(recipeItemId,'recipeItemId',128);
  const validated=createItemPreparation({
    itemId,
    userId:'recipe-model',
    curingMethodKey,
    curingMethodCustom,
    curingDurationMinutes,
    curingNotes,
    marinadeNotes,
    spicesNotes,
    dryingDurationMinutes,
    dryingNotes,
    preparationDurationMinutes,
    preparationNotes
  });
  return Object.freeze({
    recipeItemId:itemId,
    curingMethodKey:validated.curingMethodKey,
    curingMethodCustom:validated.curingMethodCustom,
    curingDurationMinutes:validated.curingDurationMinutes,
    curingNotes:validated.curingNotes,
    marinadeNotes:validated.marinadeNotes,
    spicesNotes:validated.spicesNotes,
    dryingDurationMinutes:validated.dryingDurationMinutes,
    dryingNotes:validated.dryingNotes,
    preparationDurationMinutes:validated.preparationDurationMinutes,
    preparationNotes:validated.preparationNotes,
    processDurationScaleMode:'fixed'
  });
}

export function scaleRecipeItem(item,factor){
  const value=createRecipeItem(item);
  const multiplier=Number(factor);
  if(!Number.isFinite(multiplier)||multiplier<=0)throw new TypeError('scale factor must be positive');
  if(value.minScaleFactor!=null&&multiplier<value.minScaleFactor)throw new TypeError('scale factor is below the recipe limit');
  if(value.maxScaleFactor!=null&&multiplier>value.maxScaleFactor)throw new TypeError('scale factor exceeds the recipe limit');
  if(value.scaleMode===RecipeItemScaleMode.FIXED)return value;
  return createRecipeItem({
    ...value,
    referenceQuantity:value.referenceQuantity==null?null:value.referenceQuantity*multiplier,
    referenceMass:value.referenceMass==null?null:value.referenceMass*multiplier
  });
}

export function recipeItemToRow(item){
  const value=createRecipeItem(item);
  return Object.freeze({
    id:value.id,
    recipe_id:value.recipeId,
    catalogue_product_code:value.catalogueProductCode,
    custom_product_code:value.customProductCode,
    name_snapshot:value.name,
    reference_quantity:value.referenceQuantity,
    reference_mass:value.referenceMass,
    mass_unit:value.massUnit,
    scale_mode:value.scaleMode,
    min_scale_factor:value.minScaleFactor,
    max_scale_factor:value.maxScaleFactor,
    scaling_notes:value.scalingNotes,
    sort_order:value.sortOrder
  });
}

export function recipeItemFromRow(row={}){
  return createRecipeItem({
    id:row.id,
    recipeId:row.recipe_id,
    catalogueProductCode:row.catalogue_product_code,
    customProductCode:row.custom_product_code,
    name:row.name_snapshot,
    referenceQuantity:row.reference_quantity,
    referenceMass:row.reference_mass,
    massUnit:row.mass_unit,
    scaleMode:row.scale_mode,
    minScaleFactor:row.min_scale_factor,
    maxScaleFactor:row.max_scale_factor,
    scalingNotes:row.scaling_notes,
    sortOrder:row.sort_order
  });
}

export function recipePreparationToRow(preparation){
  const value=createRecipeItemPreparation(preparation);
  return Object.freeze({
    recipe_item_id:value.recipeItemId,
    curing_method_key:value.curingMethodKey,
    curing_method_custom:value.curingMethodCustom,
    curing_duration_minutes:value.curingDurationMinutes,
    curing_notes:value.curingNotes,
    marinade_notes:value.marinadeNotes,
    spices_notes:value.spicesNotes,
    drying_duration_minutes:value.dryingDurationMinutes,
    drying_notes:value.dryingNotes,
    preparation_duration_minutes:value.preparationDurationMinutes,
    preparation_notes:value.preparationNotes,
    process_duration_scale_mode:value.processDurationScaleMode
  });
}
